import { useEffect, useState, useCallback, useMemo } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useTranslation } from "react-i18next";
import { BonusInfoFooter } from "../../../common/components/BonusInfoFooter/BonusInfoFooter";
import { ActionReportHeader } from "../common/Header/ActionReportHeader";
import { ActionReportToolbar } from "../common/Toolbar/ActionReportToolbar";
import { BirthdayGroup, BirthdayEntry } from "./BirthdayGroup/BirthdayGroup";
import { MODULE_BIRTHDAYS_V2 } from "../../../common/constants/export-constants";
import { buildFieldTitleOverrides } from "../../../common/util/exportUtil";
import { useAppDispatch, useAppSelector } from "../../../store";
import { updatePeriod } from "../../../reducers/period";
import { toggleSortDirection } from "../../../reducers/birthdaysArReport";
import { getCurrentPeriod } from "../../../common/util/period";
import defaultContent from "../../../content/actionReportsBirthdays";
import { getCanonicalTitle } from "../../../common/util/Incentives/IncentiveRegistry";
import { ERROR_TYPE, ErrorPage, IconType } from "../../ErrorPage/ErrorPage";
import { FetchBirthdaysActionReportData } from "./BirthdaysActionReportDataLayer";
import type {
  SearchSelectItem,
  SearchCallback,
  SelectionCallback,
} from "../../../common/components/SearchSelect/SearchSelect";
import "../ActionReports.scss";
import "./BirthdaysActionReport.scss";
import { ActionReportTableLoader } from "../common/ActionReportListView/DesktopTable/DesktopTableLoader";
import ServerSideExportInProgressNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import ServerSideExportCompletedNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import { dispatchOpenExportV2ModalEvent } from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { birthdayARLoadTrack, birthdayExportTrack } from "../../../common/tealium/ActionReports/BirthdayARTrack";
import { storedLocale } from "../../Authentication/util";
import type {
  BirthdayEntry as ApiBirthdayEntry,
  RecordRef,
} from "../../../services/ActionReports/ActionReportAPI.types";
import { selectGroupLeader, setSelectedLeader } from "../../../reducers/groupLeaderFilter";
import { useGroupLeaderUrlSync } from "../../../common/hooks/useGroupLeaderUrlSync";
import { GroupLeaderFilter } from "../common/GroupLeaderFilter/GroupLeaderFilter";
import { GroupLeaderFilterErrorBoundary } from "../common/GroupLeaderFilter/GroupLeaderFilterErrorBoundary";
import { recordsInLeaderSubtree, leaderExistsInTree, parentLeaderOf } from "../common/groupLeaderGrouping";
import { ActionReportMobileCardLoader } from "../common/ActionReportListView/MobileTable/MobileTable";
import { getCountryNameFromAff } from "../../../common/util/formatCountryName";

const padAffSafe = (aff: string): string => {
  if (aff.length >= 3) return aff;
  return /^\d+$/.test(aff) ? aff.padStart(3, "0") : aff;
};

export interface GroupedBirthdays {
  currentMonth: BirthdayEntry[];
  nextMonth: BirthdayEntry[];
  nextTwoMonths: BirthdayEntry[];
}

export default function BirthdaysActionReport() {
  const { t } = useTranslation(["actionReportsBirthdays", "actionReports"]);
  const dispatch = useAppDispatch();
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { sortDirection } = useAppSelector((state) => state.birthdaysActionReportTracker);
  const confidentialUser = t("confidentialUser", "Confidential User");
  const selectedLeader = useAppSelector(selectGroupLeader);

  const pageTitle = getCanonicalTitle({ id: "birthdays" }, t, {});
  const enableGroupLeaderFilter = useAppSelector(
    (state) => state.boot.configuration.actionReports.enableGroupLeaderFilter,
  );
  const { birthdaysActionReportData, isLoading, errorStatus } = FetchBirthdaysActionReportData(selectedPeriod);

  const groupLeaders = useMemo(
    () => birthdaysActionReportData.groupLeaders ?? [],
    [birthdaysActionReportData.groupLeaders],
  );
  const groupLeaderTree = useMemo(
    () => birthdaysActionReportData.groupLeaderTree ?? [],
    [birthdaysActionReportData.groupLeaderTree],
  );
  // Pass the raw (possibly undefined) field so the hook defers URL hydration
  // until the leader list has actually loaded — otherwise a deep-link `?gl=`
  // can be cleared against the pre-fetch empty default.
  useGroupLeaderUrlSync(isLoading ? undefined : birthdaysActionReportData.groupLeaders);
  const isMobile = useIsMobile();

  const currentMonth = birthdaysActionReportData.reportRecords.currentMonth;
  const nextMonth = birthdaysActionReportData.reportRecords.nextMonth;
  const nextTwoMonths = birthdaysActionReportData.reportRecords.nextTwoMonths;

  const hasNoBirthdays =
    !isLoading && currentMonth.length === 0 && nextMonth.length === 0 && nextTwoMonths.length === 0;

  // Track when all data is rendered and sort is safe to use
  const [isSortReady, setIsSortReady] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setIsSortReady(false);
      return;
    }
    // Wait for two animation frames to ensure Virtuoso has rendered all rows
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        setIsSortReady(!hasNoBirthdays);
      });
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, [isLoading, sortDirection, hasNoBirthdays]);

  // Search state
  const [searchText, setSearchText] = useState<string>("");
  const [scrollToAbo, setScrollToAbo] = useState<
    { aff: string; abo: string; group: "currentMonth" | "nextMonth" | "nextTwoMonths" } | undefined
  >(undefined);

  useEffect(() => {
    dispatch(updatePeriod({ period: getCurrentPeriod() }));
  }, [dispatch]);

  useEffect(() => {
    birthdayARLoadTrack(selectedPeriod);
  }, [selectedPeriod]);

  const handleSortClick = useCallback(() => {
    // Don't scroll to top if a table row scroll is in progress
    if (!window.__tableRowScrollInProgress) {
      // Scroll to top so all tables render from the beginning
      window.scrollTo({ top: 0, behavior: "instant" });
    }
    setIsSortReady(false);
    dispatch(toggleSortDirection());
    // After React commits the new sort order, fire a scroll event so Virtuoso
    // recalculates which rows are in the viewport and renders them
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event("scroll"));
      });
    });
  }, [dispatch]);

  const notifyDownloadLinkOnClick = useCallback(() => {
    dispatchOpenExportV2ModalEvent(MODULE_BIRTHDAYS_V2, true);
  }, []);

  const sections = t("sections", {
    returnObjects: true,
    defaultValue: defaultContent.sections,
  }) as { thisMonth: string; birthdaysNextMonth: string; birthdayInMonth: string };

  const nextTwoMonthsDate = new Date();
  nextTwoMonthsDate.setMonth(nextTwoMonthsDate.getMonth() + 2);
  const nextTwoMonthsName = new Intl.DateTimeFormat(storedLocale(), { month: "long" })
    .format(nextTwoMonthsDate)
    .replace(/^\p{L}/u, (c) => c.toLocaleUpperCase(storedLocale()));

  const getFirstGroupLeader = (e: ApiBirthdayEntry): RecordRef | undefined => e.firstGroupLeader;
  const getSelfRef = (e: ApiBirthdayEntry): RecordRef => ({ aff: e.aff, abo: e.abo });

  // Create search pool from all birthday entries across all groups
  type BirthdaySearchEntry = {
    abo: string;
    aff: string;
    aboName: string;
    group: "currentMonth" | "nextMonth" | "nextTwoMonths";
    indexInGroup: number;
    firstGroupLeader?: RecordRef;
  };

  const searchPool = useMemo<BirthdaySearchEntry[]>(() => {
    const seen = new Set<string>();
    const groups: Array<{ group: "currentMonth" | "nextMonth" | "nextTwoMonths"; entries: ApiBirthdayEntry[] }> = [
      { group: "currentMonth", entries: currentMonth },
      { group: "nextMonth", entries: nextMonth },
      { group: "nextTwoMonths", entries: nextTwoMonths },
    ];

    return groups.flatMap(({ group, entries }) =>
      entries.reduce<BirthdaySearchEntry[]>((acc, entry, indexInGroup) => {
        const key = `${entry.aff}-${entry.abo}`;
        if (!seen.has(key)) {
          seen.add(key);
          acc.push({
            abo: entry.abo,
            aff: entry.aff,
            aboName: entry.confidentialUser ? confidentialUser : entry.aboName || "",
            group,
            indexInGroup,
            firstGroupLeader: entry.firstGroupLeader,
          });
        }
        return acc;
      }, []),
    );
  }, [currentMonth, nextMonth, nextTwoMonths, confidentialUser]);

  const handleSearchCallback = useCallback<SearchCallback>(
    (text) =>
      searchPool
        .filter((entry) => entry.aboName.toLowerCase().includes(text.toLowerCase()) || entry.abo.includes(text))
        .map((entry): SearchSelectItem => {
          const sublabel = `${entry.abo} | ${getCountryNameFromAff(entry.aff, storedLocale())}`;
          return {
            key: `${entry.aff}-${entry.abo}-${entry.group}`,
            label: entry.aboName,
            sublabel,
          };
        }),
    [searchPool],
  );

  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => {
      const found = searchPool.find((entry) => `${entry.aff}-${entry.abo}-${entry.group}` === key);
      if (found) {
        const selfRef: RecordRef = { aff: found.aff, abo: found.abo };
        const isInTree = leaderExistsInTree(groupLeaderTree, selfRef);
        const fglMatchesSelf =
          !!found.firstGroupLeader &&
          padAffSafe(found.firstGroupLeader.aff) === padAffSafe(found.aff) &&
          found.firstGroupLeader.abo === found.abo;
        const isSelfLeader = isInTree || fglMatchesSelf;

        let targetLeader: RecordRef | null = null;
        if (enableGroupLeaderFilter && groupLeaders.length > 0) {
          if (isSelfLeader) {
            targetLeader = parentLeaderOf(groupLeaderTree, selfRef);
          } else if (found.firstGroupLeader?.aff || found.firstGroupLeader?.abo) {
            targetLeader = found.firstGroupLeader;
          }
          dispatch(setSelectedLeader(targetLeader));
        }

        setScrollToAbo({ aff: found.aff, abo: found.abo, group: found.group });
        setSearchText(found.aboName);
      }
      return { displayName: found?.aboName ?? "" };
    },
    [searchPool, groupLeaderTree, groupLeaders, enableGroupLeaderFilter, dispatch],
  );

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setScrollToAbo(undefined);
  }, []);

  const exportAffAbos = [...currentMonth, ...nextMonth, ...nextTwoMonths].map((entry) => ({
    affAbo: `${entry.aff}-${entry.abo}`,
    level: 0,
    sponsorAbo: "",
    nameTypeFlag: entry.nameTypeFlag,
    birthDate: entry.birthDate,
  }));

  const startedByLabels = useMemo<Record<string, string>>(
    () => ({
      [MODULE_BIRTHDAYS_V2]: t("export.exportTypeLabel", defaultContent.export.exportTypeLabel).toString(),
    }),
    [t],
  );

  const tableColumns = t("table.columns", {
    ns: "actionReportsBirthdays",
    returnObjects: true,
    defaultValue: defaultContent.table.columns,
  }) as Record<string, string>;

  const fieldTitleOverrides = useMemo(() => buildFieldTitleOverrides(tableColumns), [tableColumns]);

  const getMobileLaoder = () => {
    return (
      <div>
        <ActionReportMobileCardLoader numRows={3} />
        <ActionReportMobileCardLoader numRows={3} />
        <ActionReportMobileCardLoader numRows={3} />
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="BirthdaysAR__content">
          {isMobile ? getMobileLaoder() : <ActionReportTableLoader columns={4} rows={3} />}
        </div>
      );
    }

    if (errorStatus) {
      return (
        <div className="BirthdaysAR__error">
          <ErrorPage errorType={ERROR_TYPE.GENERIC_ERROR} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    if (hasNoBirthdays) {
      return (
        <div className="BirthdaysAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    const renderMonthGroups = (current: ApiBirthdayEntry[], next: ApiBirthdayEntry[], nextTwo: ApiBirthdayEntry[]) => {
      const ordered = [
        { key: "currentMonth", title: sections.thisMonth, entries: current, group: "currentMonth" as const },
        { key: "nextMonth", title: sections.birthdaysNextMonth, entries: next, group: "nextMonth" as const },
        {
          key: "nextTwoMonths",
          title: t("sections.birthdayInMonth", defaultContent.sections.birthdayInMonth, { month: nextTwoMonthsName }),
          entries: nextTwo,
          group: "nextTwoMonths" as const,
        },
      ];
      const groupsInOrder = sortDirection === "asc" ? ordered : [...ordered].reverse();
      return (
        <>
          {groupsInOrder.map(({ key, title, entries, group }) => (
            <BirthdayGroup
              key={key}
              title={title}
              entries={entries}
              scrollToAbo={scrollToAbo?.group === group ? scrollToAbo : undefined}
              expandKey={selectedLeader}
              data-testid={`birthdays-group-${group}`}
            />
          ))}
        </>
      );
    };

    // Scoped view: filter each month bucket to the selected leader's
    // subtree. No GroupLeaderSection header — Figma keeps the same
    // month-bucket layout, just narrowed to the leader's records.
    if (selectedLeader && groupLeaders.length > 0) {
      const scopedCurrent = recordsInLeaderSubtree(
        currentMonth,
        groupLeaderTree,
        selectedLeader,
        getSelfRef,
        getFirstGroupLeader,
      );
      const scopedNext = recordsInLeaderSubtree(
        nextMonth,
        groupLeaderTree,
        selectedLeader,
        getSelfRef,
        getFirstGroupLeader,
      );
      const scopedNextTwo = recordsInLeaderSubtree(
        nextTwoMonths,
        groupLeaderTree,
        selectedLeader,
        getSelfRef,
        getFirstGroupLeader,
      );
      if (scopedCurrent.length === 0 && scopedNext.length === 0 && scopedNextTwo.length === 0) {
        return (
          <div className="BirthdaysAR__noDownlines">
            <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
          </div>
        );
      }
      return (
        <div className="BirthdaysAR__content" data-testid="birthdays-ar-content">
          {renderMonthGroups(scopedCurrent, scopedNext, scopedNextTwo)}
        </div>
      );
    }

    // "All" view: render the full list. Figma omits leader-name section
    // headers on this report regardless of GL filter availability.
    return (
      <div className="BirthdaysAR__content" data-testid="birthdays-ar-content">
        {renderMonthGroups(currentMonth, nextMonth, nextTwoMonths)}
      </div>
    );
  };

  return (
    <main className="BirthdaysAR">
      <ActionReportHeader
        title={pageTitle}
        description={t("title.pageDescription", defaultContent.title.pageDescription).toString()}
        reportLength={exportAffAbos.length}
      />
      <ActionReportToolbar
        reportLength={exportAffAbos.length}
        isAllEnriched={true}
        sort={{
          enabled: true,
          direction: sortDirection,
          onClick: handleSortClick,
          disabled: !isSortReady,
        }}
        search={{
          enabled: true,
          searchCallback: handleSearchCallback,
          selectionCallback: handleSelectionCallback,
          searchText: searchText,
          clearFunction: handleClearSearch,
          disabled: isLoading || searchPool.length === 0,
        }}
        export={{
          moduleId: MODULE_BIRTHDAYS_V2,
          exportAffAbos,
          selectedKpis: ["birthDate", "bonusPercent", "groupPV"],
          confirmationMessage: t("export.description", defaultContent.export.description).toString(),
          exportType: MODULE_BIRTHDAYS_V2,
          startedByLabels,
          fieldTitleOverrides,
        }}
        exportTrack={birthdayExportTrack}
        groupLeaderFilterSlot={
          enableGroupLeaderFilter ? (
            isLoading ? (
              <GroupLeaderFilter leaders={[]} tree={[]} isLoading data-testid="birthdays-group-leader-filter" />
            ) : groupLeaders.length > 0 ? (
              <GroupLeaderFilterErrorBoundary>
                <GroupLeaderFilter
                  leaders={groupLeaders}
                  tree={groupLeaderTree}
                  allOptionLabel={t(
                    "filter.groupLeader.allOption",
                    defaultContent.filter.groupLeader.allOption,
                  ).toString()}
                  data-testid="birthdays-group-leader-filter"
                />
              </GroupLeaderFilterErrorBoundary>
            ) : undefined
          ) : undefined
        }
      />
      {renderContent()}
      <div>
        <BonusInfoFooter />
      </div>
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify
        moduleId={MODULE_BIRTHDAYS_V2}
        notifyDownloadLinkOnClick={notifyDownloadLinkOnClick}
      />
    </main>
  );
}
